import React, { useState, useEffect } from "react";
import {
    Modal,
    Box,
    Typography,
    Button,
    InputLabel,
    Input,
    Stack,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Alert,
} from "@mui/material";
import * as XLSX from "xlsx";
import axios from "axios";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import * as FileSaver from "file-saver";
import TablePagination from "@mui/material/TablePagination";
import { useAuth } from "./TokenHandler";

const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 1600,
    maxHeight: "80vh",
    overflowY: "auto",
    bgcolor: "background.paper",
    borderRadius: "8px",
    boxShadow: 24,
    p: 4,
};

const REQUIRED_COLUMNS = [
    "Provider ID", "Facility", "Resident Last Name", "Resident First Name", "DOB", "MRN",
    "CCM", "CCM Date", "CCM Reason", "RPM", "RPM Date", "RPM Reason",
    "BHI", "BHI Date", "BHI Reason", "Notes (Free Text)",
];

const RESPONSE_COLUMN_ORDER = [
    "status", "Provider ID", "Facility", "Resident Last Name", "Resident First Name", "DOB", "MRN",
    "CCM", "CCM Date", "CCM Reason", "RPM", "RPM Date", "RPM Reason",
    "BHI", "BHI Date", "BHI Reason", "Notes (Free Text)",
    "fac_id", "ouid", "patient_id", "gender", "Created by", "created date",  "batch_id",
];

const DATE_FIELDS = ["DOB", "CCM Date", "RPM Date", "BHI Date"];
const HIDDEN_COLUMNS = ["fac_id", "ouid", "patient_id", "gender"];

// ---- Robust date parsing ----
function toISODate(y, m, d) {
    const mm = String(m).padStart(2, "0");
    const dd = String(d).padStart(2, "0");
    return `${y}-${mm}-${dd}`;
}

function excelSerialToISO(n) {
    // compatible with XLSX.SSF.parse_date_code but simpler if SSF not present
    const parsed = XLSX.SSF.parse_date_code(n);
    if (!parsed) return "";
    return toISODate(parsed.y, parsed.m, parsed.d);
}

function smartDateToISO(v) {
    if (v == null || v === "") return "";
    if (typeof v === "number") return excelSerialToISO(v);
    if (v instanceof Date && !isNaN(v)) {
        return toISODate(v.getFullYear(), v.getMonth() + 1, v.getDate());
    }
    // Try to parse common string formats (assume M/D/Y or YYYY-MM-DD)
    const s = String(v).trim();
    // YYYY-MM-DD
    const m1 = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (m1) return s;
    // M/D/YYYY or MM/DD/YYYY
    const m2 = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/.exec(s);
    if (m2) return toISODate(Number(m2[3]), Number(m2[1]), Number(m2[2]));
    return s; // fallback: keep as-is
}

// ---- Canon + header finding ----
const canon = (s) =>
    String(s || "")
        .replace(/\u00A0/g, " ")
        .trim()
        .replace(/\s+/g, " ")
        .toLowerCase();

function pickBestSheet(workbook) {
    // choose the sheet with the largest used range
    let bestName = workbook.SheetNames[0];
    let bestSize = -1;
    for (const name of workbook.SheetNames) {
        const ws = workbook.Sheets[name];
        if (!ws || !ws['!ref']) continue;
        const range = XLSX.utils.decode_range(ws['!ref']);
        const size = (range.e.r - range.s.r + 1) * (range.e.c - range.s.c + 1);
        if (size > bestSize) {
            bestSize = size;
            bestName = name;
        }
    }
    return bestName;
}

function findHeaderRow(rawAoa, requiredCanon, searchRows = 50) {
    // Look through the first N rows to find the one that contains most/all required columns
    let bestRow = 0;
    let bestHit = -1;
    const limit = Math.min(rawAoa.length, searchRows);
    for (let r = 0; r < limit; r++) {
        const row = (rawAoa[r] || []).map(canon);
        // count matches
        const hits = requiredCanon.reduce((acc, req) => acc + (row.includes(req) ? 1 : 0), 0);
        if (hits > bestHit) {
            bestHit = hits;
            bestRow = r;
            if (hits === requiredCanon.length) break; // perfect match
        }
    }
    return bestRow;
}


// --- helpers ---
const generateUUID = () =>
    (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
            const r = (Math.random() * 16) | 0, v = c === "x" ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });


function chunkArray(arr, size) {
    const out = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
}

function makeBatchTask({ payload, index, url, authToken, firstName, lastName, todayStr }) {
    return async () => {
        try {
            const resp = await axios.post(url, payload, {
                headers: {
                    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
                    "Content-Type": "application/json",
                },
            });

            const rows =
                resp?.data?.postProcessedPatients ??
                resp?.data?.records ??
                resp?.data ??
                [];

            if (!Array.isArray(rows)) {
                throw new Error("Unexpected API response format.");
            }

            const normalized = rows.map((r) => ({
                "Created by": r["Created by"] ?? `${firstName} ${lastName}`.trim(),
                "created date": r["created date"] ?? todayStr,
                ...r,
            }));

            return { ok: true, index, data: normalized };
        } catch (err) {
            const message =
                err?.response?.data?.error ||
                err?.message ||
                "Batch request failed.";
            return { ok: false, index, message };
        }
    };
}

async function runWithConcurrencyLimit(tasks, limit, onWindowDone) {
    // Run tasks in windows of size `limit`
    const allResults = [];
    for (let i = 0; i < tasks.length; i += limit) {
        const windowTasks = tasks.slice(i, i + limit).map((fn) => fn());
        const settled = await Promise.allSettled(windowTasks);
        // Flatten results from each promise (fulfilled value is the task's return)
        const windowResults = settled.map((r) =>
            r.status === "fulfilled" ? r.value : { ok: false, index: -1, message: "Unexpected promise rejection" }
        );
        allResults.push(...windowResults);

        // Allow caller to update progress/state per window
        if (onWindowDone) onWindowDone(windowResults.length, windowResults);
    }
    return allResults;
}


const UploadSpreadsheetModal = ({ open, onClose }) => {
    const {
        keycloak: kc,
        infoMessage,
        setInfoMessage,
        givenName,
        email,
        roles,
        // auto pause:
        setAutoLogoutPaused,
        // auto pause:
    } = useAuth();

    const firstName = kc?.tokenParsed?.given_name ?? givenName ?? "(unknown)";
    const lastName = kc?.tokenParsed?.family_name ?? "(unknown)";

    const [parsedData, setParsedData] = useState([]);
    const [missingColumns, setMissingColumns] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [apiError, setApiError] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);

    // progress & per-batch error tracking
    const [totalBatches, setTotalBatches] = useState(0);
    const [completedBatches, setCompletedBatches] = useState(0);
    const [batchErrors, setBatchErrors] = useState([]); // array of {index, message}


    //auto pause
    useEffect(() => {
        if (open) {
            setAutoLogoutPaused(true);
            return () => setAutoLogoutPaused(false); // safety on unmount
        }
    }, [open, setAutoLogoutPaused]);
    //auto pause

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // ---- reset UI state
        setApiError("");
        setParsedData([]);
        setMissingColumns([]);
        setBatchErrors([]);
        setCompletedBatches(0);
        setTotalBatches(0);

        // ---- in-scope helpers (self-contained)
        const makeBatchTask = ({ payload, index, url, authToken, firstName, lastName, todayStr }) => {
            return async () => {
                try {
                    const resp = await axios.post(url, payload, {
                        headers: {
                            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
                            "Content-Type": "application/json",
                        },
                    });

                    const rows =
                        resp?.data?.postProcessedPatients ??
                        resp?.data?.records ??
                        resp?.data ??
                        [];

                    if (!Array.isArray(rows)) {
                        throw new Error("Unexpected API response format.");
                    }

                    const normalized = rows.map((r) => ({
                        "Created by": r["Created by"] ?? `${firstName} ${lastName}`.trim(),
                        "created date": r["created date"] ?? todayStr,
                        ...r,
                    }));

                    return { ok: true, index, data: normalized };
                } catch (err) {
                    const message =
                        err?.response?.data?.error ||
                        err?.message ||
                        "Batch request failed.";
                    return { ok: false, index, message };
                }
            };
        };

        const runWithConcurrencyLimit = async (tasks, limit, onWindowDone) => {
            const allResults = [];
            for (let i = 0; i < tasks.length; i += limit) {
                const windowTasks = tasks.slice(i, i + limit).map((fn) => fn());
                const settled = await Promise.allSettled(windowTasks);

                const windowResults = settled.map((r) =>
                    r.status === "fulfilled" ? r.value : { ok: false, index: -1, message: "Unexpected promise rejection" }
                );

                allResults.push(...windowResults);
                if (onWindowDone) onWindowDone(windowResults.length, windowResults);
            }
            return allResults;
        };

        const reader = new FileReader();
        reader.onload = async (event) => {
            try {
                // ---- 1) Parse workbook robustly
                const data = new Uint8Array(event.target.result);
                const workbook = XLSX.read(data, {
                    type: "array",
                    dense: true,          // avoid sparse holes
                    cellDates: true,      // produce Date objects if possible
                    cellNF: false,
                    cellText: false
                });

                if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
                    setMissingColumns(["No sheets found in workbook"]);
                    return;
                }

                const bestSheetName = pickBestSheet(workbook);
                const worksheet = workbook.Sheets[bestSheetName];

                // AOA with defaults for empty cells to avoid undefined / __EMPTY artifacts
                const rawAoa = XLSX.utils.sheet_to_json(worksheet, {
                    header: 1,
                    raw: true,
                    defval: "",
                    blankrows: false
                });

                if (!rawAoa.length) {
                    setMissingColumns(["File is empty or could not be parsed"]);
                    return;
                }

                // ---- 1a) Find the real header row
                const REQUIRED_CANON = REQUIRED_COLUMNS.map(canon);
                const headerRowIdx = findHeaderRow(rawAoa, REQUIRED_CANON, 50);
                const incomingHeadersRaw = (rawAoa[headerRowIdx] || []).map(h => String(h || "").trim());

                // Map incoming header -> official header
                const incomingCanon = incomingHeadersRaw.map(canon);
                const headerMap = {};
                incomingHeadersRaw.forEach((orig, i) => {
                    const c = incomingCanon[i];
                    const matchIdx = REQUIRED_CANON.indexOf(c);
                    headerMap[orig] = matchIdx !== -1 ? REQUIRED_COLUMNS[matchIdx] : orig;
                });

                // Use normalized headers downstream
                const headers = incomingHeadersRaw.map((h) => headerMap[h]);

                // ---- 1b) Validate required columns
                const missing = REQUIRED_COLUMNS.filter((col) => !headers.includes(col));
                if (missing.length > 0) {
                    setMissingColumns(missing);
                    return;
                }

                // ---- 2) Sanitize rows
                const today = new Date();
                const yyyy = today.getFullYear();
                const mm = String(today.getMonth() + 1).padStart(2, "0");
                const dd = String(today.getDate()).padStart(2, "0");
                const todayStr = `${yyyy}-${mm}-${dd}`;

                const batchId = generateUUID();

                const deNBSP = (v) => String(v ?? "").replace(/\u00A0/g, " ").trim();

                const cleanFirst = (v) => {
                    let s = deNBSP(v);
                    s = s.split(/[;,/]/)[0];
                    s = s.replace(/\.\s*$/, "");
                    s = s.replace(/\s+[A-Za-z]$/, "");
                    return s.trim();
                };
                const cleanLast = (v) => {
                    let s = deNBSP(v);
                    s = s.split(",")[0];
                    return s.trim();
                };

                // Data rows start after headerRowIdx
                const dataRows = rawAoa.slice(headerRowIdx + 1);

                const sanitizedData = dataRows.map((rowArr) => {
                    const rowData = {};
                    // init with known headers
                    headers.forEach((h) => (rowData[h] = ""));
                    // copy cell values aligned with incomingHeadersRaw
                    incomingHeadersRaw.forEach((incomingHeader, i) => {
                        const officialKey = headerMap[incomingHeader];
                        const rawVal = rowArr[i];

                        if (DATE_FIELDS.includes(officialKey)) {
                            rowData[officialKey] = smartDateToISO(rawVal);
                        } else {
                            rowData[officialKey] = deNBSP(rawVal);
                        }
                    });

                    if (rowData["Resident First Name"]) {
                        rowData["Resident First Name"] = cleanFirst(rowData["Resident First Name"]);
                    }
                    if (rowData["Resident Last Name"]) {
                        rowData["Resident Last Name"] = cleanLast(rowData["Resident Last Name"]);
                    }

                    // ensure all required fields exist
                    REQUIRED_COLUMNS.forEach((req) => {
                        if (!(req in rowData)) rowData[req] = "";
                    });

                    rowData["Created by"]   = `${firstName} ${lastName}`.trim();
                    rowData["created date"] = todayStr;
                    rowData["batch_id"]     = batchId;

                    return rowData;
                });

                // Diagnostics (optional)
                const uniqueBatchIds = Array.from(new Set(sanitizedData.map(r => r.batch_id)));
                if (uniqueBatchIds.length !== 1) {
                    console.warn("[UploadSpreadsheetModal] Unexpected batch_id spread:", uniqueBatchIds);
                }

                // ---- 3) Batch + POST with concurrency=2 (unchanged)
                const batches = chunkArray(sanitizedData, 20);
                setTotalBatches(batches.length);
                setIsLoading(true);

                const url = `${process.env.REACT_APP_API_URL}/bulk-optouts2`;
                const tasks = batches.map((payload, i) =>
                    makeBatchTask({
                        payload,
                        index: i + 1,
                        url,
                        authToken: kc?.token,
                        firstName,
                        lastName,
                        todayStr,
                    })
                );

                const results = await runWithConcurrencyLimit(
                    tasks,
                    2,
                    (windowSize, windowResults) => {
                        setCompletedBatches((prev) => prev + windowSize);
                        const errs = windowResults
                            .filter((r) => !r.ok)
                            .map(({ index, message }) => ({ index, message }));
                        if (errs.length) setBatchErrors((prev) => [...prev, ...errs]);
                    }
                );

                const allRows = results.filter((r) => r.ok).flatMap((r) => r.data);
                setParsedData(allRows);

                if (!allRows.length && results.some((r) => !r.ok)) {
                    setApiError("All batches failed. Please check errors and try again.");
                }
            } catch (err) {
                console.error("Upload/processing failed:", err);
                setApiError(
                    err?.response?.data?.error ||
                    err?.message ||
                    "Failed to upload/process data. Please try again."
                );
                setParsedData([]);
            } finally {
                setIsLoading(false);
            }
        };


        reader.readAsArrayBuffer(file);
    };


    const handleClose = () => {
        //auto pause
        setAutoLogoutPaused(false); // NEW
        //auto pause
        setParsedData([]);
        setMissingColumns([]);
        setApiError("");
        setBatchErrors([]);
        setCompletedBatches(0);
        setTotalBatches(0);
        onClose();
    };

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleDownload = () => {
        if (parsedData.length === 0) return;

        const visibleColumns = RESPONSE_COLUMN_ORDER.filter(
            (col) => !HIDDEN_COLUMNS.includes(col)
        );

        const filteredData = parsedData.map((row) => {
            const newRow = {};
            visibleColumns.forEach((col) => {
                newRow[col] = row[col] !== undefined ? row[col] : "";
            });
            return newRow;
        });

        const worksheet = XLSX.utils.json_to_sheet(filteredData, {
            header: visibleColumns,
        });
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Results");

        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
        FileSaver.saveAs(blob, "bulk-optout-results.xlsx");
    };

    return (
        <Modal open={open} onClose={handleClose} aria-labelledby="upload-spreadsheet-title">
            <Box sx={modalStyle}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography id="upload-spreadsheet-title" variant="h6">
                        Upload Spreadsheet
                    </Typography>
                    <Box>
                        <IconButton
                            onClick={handleDownload}
                            disabled={parsedData.length === 0}
                            title="Download Results"
                        >
                            <DownloadIcon />
                        </IconButton>
                        <IconButton onClick={handleClose} title="Close Modal">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>

                <Stack spacing={2}>
                    <InputLabel htmlFor="upload-file-input">Choose CSV or Excel file</InputLabel>
                    <Input
                        id="upload-file-input"
                        type="file"
                        inputProps={{ accept: ".csv, .xlsx, .xls" }}
                        onChange={handleFileChange}
                        disabled={isLoading}
                    />

                    {apiError && <Alert severity="error">{apiError}</Alert>}

                    {missingColumns.length > 0 && (
                        <Alert severity="error">
                            Missing Required Columns: {missingColumns.join(", ")}
                        </Alert>
                    )}

                    {batchErrors.length > 0 && (
                        <Alert severity="warning">
                            Some batches failed:
                            <ul style={{ marginTop: 8 }}>
                                {batchErrors.map(({ index, message }, i) => (
                                    <li key={`${index}-${i}`}>Batch {index}: {message}</li>
                                ))}
                            </ul>
                        </Alert>
                    )}

                    {isLoading && (
                        <Box mt={2} display="flex" alignItems="center" gap={2}>
                            <CircularProgress />
                            <Typography>
                                Processing data… {completedBatches}/{totalBatches} batches completed
                            </Typography>
                        </Box>
                    )}

                    {parsedData.length > 0 && !isLoading && (
                        <Box mt={2}>
                            <Typography variant="subtitle1" mb={1}>Preview Data:</Typography>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        {RESPONSE_COLUMN_ORDER.filter(col => !HIDDEN_COLUMNS.includes(col)).map((header) => (
                                            <TableCell key={header}>{header}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {parsedData
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row, rowIndex) => (
                                            <TableRow key={rowIndex}>
                                                {RESPONSE_COLUMN_ORDER
                                                    .filter(col => !HIDDEN_COLUMNS.includes(col))
                                                    .map((col, colIndex) => (
                                                        <TableCell key={colIndex}>
                                                            {row[col] !== undefined ? row[col] : ""}
                                                        </TableCell>
                                                    ))}
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                            <Typography variant="caption" color="textSecondary">
                                <TablePagination
                                    rowsPerPageOptions={[25, 50, 100, 200]}
                                    component="div"
                                    count={parsedData.length}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                />
                            </Typography>
                        </Box>
                    )}

                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button variant="outlined" onClick={handleClose}>Close</Button>
                    </Stack>
                </Stack>
            </Box>
        </Modal>
    );
};

export default UploadSpreadsheetModal;
